import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { ViewerQuery } from "core/queries";
import { CheckSMobileMutation } from "arena/mutations";
import { getUtmFromLocalStorage } from "core/utils/utm";

/**
 * Компонент для активации SMobile подписки.
 * Самодостаточный компонент, который:
 * - Получает smobileIsdn из URL параметров
 * - Проверяет статус авторизации пользователя
 * - Показывает диалог авторизации для неавторизованных
 * - Показывает загрузку и выполняет мутацию для авторизованных
 * - Обрабатывает успех/ошибки активации
 *
 * Требуемые i18n ключи (namespace: arena):
 * - smobile.signInDescription: "Введите ваш email для активации подписки SMobile"
 * - smobile.activating: "Активируем вашу подписку..."
 * - smobile.activationSuccess: "Подписка SMobile успешно активирована"
 */
export default function SMobileActivator({ dispatch, refetch }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const smobileIsdn = searchParams.get("smobileIsdn");
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation("arena");
  const smobileCheckedRef = useRef(false);

  // Получаем информацию о viewer и статус загрузки
  const { data: viewerData, loading: viewerLoading } = useQuery(ViewerQuery);
  const viewer = viewerData?.viewer;

  // Мутация для активации SMobile
  const [checkSMobile] = useMutation(CheckSMobileMutation, {
    onCompleted: (data) => {
      if (data.checkSMobile.success) {
        // Показываем успешное сообщение
        enqueueSnackbar(
          data.checkSMobile.message || t("smobile.activationSuccess"),
          { variant: "success" }
        );

        // Удаляем параметр из URL
        searchParams.delete("smobileIsdn");
        setSearchParams(searchParams);

        // Обновляем данные продукта
        refetch();

        // Закрываем диалог
        dispatch({ type: "close_dialog" });
      }
    },
    onError: (error) => {
      // Показываем ошибку
      enqueueSnackbar(
        error.message || "Failed to activate SMobile subscription",
        { variant: "error" }
      );

      // Закрываем диалог
      dispatch({ type: "close_dialog" });
    },
  });

  // Показываем диалог авторизации для неавторизованных пользователей с smobileIsdn
  useEffect(() => {
    if (!viewerLoading && smobileIsdn && !viewer) {
      dispatch({
        type: "show_signin",
        autoCloseAfterSignIn: true,
        description: t("smobile.signInDescription"),
      });
    }
  }, [smobileIsdn, viewer, viewerLoading, dispatch, t]);

  // Вызываем мутацию для авторизованных пользователей
  useEffect(() => {
    if (!viewerLoading && smobileIsdn && viewer && !smobileCheckedRef.current) {
      smobileCheckedRef.current = true;

      // Показываем диалог загрузки
      dispatch({ type: "show_smobile_loading" });

      // Выполняем мутацию
      checkSMobile({
        variables: {
          isdn: smobileIsdn,
          utm: getUtmFromLocalStorage(),
        },
      });
    }
  }, [smobileIsdn, viewer, viewerLoading, checkSMobile, dispatch]);

  // Компонент не рендерит ничего в DOM
  return null;
}

SMobileActivator.propTypes = {
  dispatch: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
};
